// ── storeHealth.js — 매장 건강 점수 (100점) ─────────────────────
// 7개 지표 가중치:
//   1. 순이익률 25점 (30%↑ 만점)
//   2. 원가율 20점 (35%↓ 만점)
//   3. 인건비 비율 15점 (25%↓ 만점)
//   4. 매출 성장 15점 (전월 대비 +10%↑ 만점)
//   5. 데이터 입력 충실도 10점 (OCR·발주·재고 입력 빈도)
//   6. 재고 회전율 10점 (안전재고 미달 빈도)
//   7. 발주 안정성 5점 (재고 미매칭·누락)
//
// 색상:
//   80점↑ 🟢 우수 / 60~79 🟡 보통 / 60↓ 🔴 주의

import { calcAvgUsage, mergeUsage, getStatus, calcRecommendedOrder } from './inventoryAlerts';
import { calcRecipeBasedUsage } from './recipeUsage';
import { investmentsOf, depForMonth, startMonthOf, paybackSummary, addMonths, monthDiff } from './investment';   // V170

// ── 단일 지표 결과 ──
function makeIndicator(id, label, weight, value, score, hint) {
  return { id, label, weight, value, score: Math.round(score * 10) / 10, hint };
}

// 1. 순이익률 (25점) — 현재 월이면 매출 run-rate 외삽으로 비교
function scoreNetProfitMargin(record, monthKey, dep = 0) {
  const rev = projectedRevenue(record, monthKey);
  const cost = sumCost(record) + (Number(dep) || 0);   // V170 감가상각비 포함
  if (rev <= 0) return makeIndicator('netMargin', '순이익률', 25, null, 0, '매출 데이터 입력 필요');
  const margin = ((rev - cost) / rev) * 100;
  // 30%+ = 만점, 0% = 0점, 음수 = 0점
  const score = Math.max(0, Math.min(25, (margin / 30) * 25));
  const hint = margin >= 30 ? '✅ 우수'
    : margin >= 15 ? '⚠️ 평균 — 비용 점검 권장'
    : margin >= 0 ? '🔴 낮음 — 개선 필요'
    : '🚨 손실 — 즉시 점검';
  return makeIndicator('netMargin', '순이익률', 25, margin, score, hint);
}

// 2. 원가율 (20점) — 현재 월이면 매출 run-rate 외삽으로 비교
function scoreFoodCost(record, monthKey) {
  const rev = projectedRevenue(record, monthKey);
  const ing = Number(record?.costs?.ingredients) || 0;
  if (rev <= 0) return makeIndicator('foodCost', '원가율', 20, null, 0, '매출 입력 필요');
  if (ing <= 0) return makeIndicator('foodCost', '원가율', 20, null, 0, '식재료비 입력 필요');
  const ratio = (ing / rev) * 100;
  // 35%↓ 만점, 50%↑ 0점
  const score = ratio <= 35 ? 20 : ratio >= 50 ? 0 : ((50 - ratio) / 15) * 20;
  const hint = ratio <= 35 ? '✅ 우수'
    : ratio <= 42 ? '⚠️ 평균 — 식자재 가격 점검'
    : '🔴 높음 — 공동구매·메뉴 가격 검토';
  return makeIndicator('foodCost', '원가율', 20, ratio, score, hint);
}

// 3. 인건비 비율 (15점) — 현재 월이면 매출 run-rate 외삽으로 비교
function scoreLaborCost(record, monthKey) {
  const rev = projectedRevenue(record, monthKey);
  const labor = Number(record?.costs?.labor) || 0;
  if (rev <= 0) return makeIndicator('laborCost', '인건비 비율', 15, null, 0, '매출 입력 필요');
  if (labor <= 0) return makeIndicator('laborCost', '인건비 비율', 15, null, 0, '인건비 입력 필요');
  const ratio = (labor / rev) * 100;
  // 25%↓ 만점, 40%↑ 0점
  const score = ratio <= 25 ? 15 : ratio >= 40 ? 0 : ((40 - ratio) / 15) * 15;
  const hint = ratio <= 25 ? '✅ 우수'
    : ratio <= 32 ? '⚠️ 평균 — 스케줄 점검'
    : '🔴 높음 — 매출 대비 직원 수 검토';
  return makeIndicator('laborCost', '인건비 비율', 15, ratio, score, hint);
}

// ── 동 기간 income 합산 (transactions 기반, 1일~targetDay) ──
function sumIncomeUpToDay(transactions, monthKey, targetDay) {
  if (!monthKey || targetDay < 1) return 0;
  const startStr = `${monthKey}-01`;
  const endStr = `${monthKey}-${String(targetDay).padStart(2, '0')}`;
  let sum = 0;
  for (const t of (transactions || [])) {
    if (t?.type !== 'income') continue;
    const d = String(t?.date || '').slice(0, 10);
    if (d >= startStr && d <= endStr) {
      sum += (Number(t.supply) || 0) + (Number(t.vat) || 0);
    }
  }
  return sum;
}

// 4. 매출 성장 (15점) — 현재 월이면 동 기간(1~오늘) prorate 비교
function scoreRevenueGrowth(record, prevRecord, ctx = {}) {
  const { transactions = [], monthKey, prevMonthKey } = ctx;
  // 현재 진행 중인 월인지 판단
  const now = new Date();
  const todayMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonth = monthKey && monthKey === todayMonthKey;
  const targetDay = isCurrentMonth ? now.getDate() : 31;

  const cur = isCurrentMonth
    ? sumIncomeUpToDay(transactions, monthKey, targetDay)
    : sumRevenue(record);
  const prev = isCurrentMonth
    ? sumIncomeUpToDay(transactions, prevMonthKey, targetDay)
    : sumRevenue(prevRecord);

  if (prev <= 0) return makeIndicator('growth', '매출 성장', 15, null, 0, '지난달 매출 입력 필요');
  if (cur <= 0) return makeIndicator('growth', '매출 성장', 15, null, 0, '이번달 매출 입력 필요');
  const delta = ((cur - prev) / prev) * 100;
  const score = delta >= 10 ? 15 : delta <= -10 ? 0 : ((delta + 10) / 20) * 15;
  const periodTag = isCurrentMonth ? `(${targetDay}일까지)` : '';
  const hint = delta >= 10 ? `✅ 성장 (+${delta.toFixed(1)}%) ${periodTag}`.trim()
    : delta >= 0 ? `⚠️ 보합 (+${delta.toFixed(1)}%) ${periodTag}`.trim()
    : `🔴 감소 (${delta.toFixed(1)}%) ${periodTag} — 마케팅·메뉴 점검`.trim();
  return makeIndicator('growth', '매출 성장', 15, delta, score, hint);
}

// 5. 데이터 입력 충실도 (10점)
function scoreDataDiligence({ orderHistory, monthKey, inventory, prices }) {
  const monthOrders = (orderHistory || []).filter(e => e?.date?.slice(0, 7) === monthKey).length;
  const inventoryItems = Object.keys(inventory?.items || {}).filter(k => {
    const v = inventory.items[k];
    return v.currentStock !== undefined || v.safetyStock > 0;
  }).length;
  const priceCount = (prices || []).length;

  // 발주 4건↑ + 재고 5개↑ 입력 + 단가표 10개↑ → 만점
  const orderScore = Math.min(1, monthOrders / 4) * 4;       // 4점
  const invScore = Math.min(1, inventoryItems / 5) * 3;      // 3점
  const priceScore = Math.min(1, priceCount / 10) * 3;        // 3점
  const score = orderScore + invScore + priceScore;
  const totalActions = monthOrders + Math.min(inventoryItems, 10);
  const hint = score >= 8 ? '✅ 활발히 사용 중'
    : score >= 5 ? '⚠️ 더 자주 입력하면 정확도 ↑'
    : '🔴 데이터 부족 — OCR·발주·재고 입력 권장';
  return makeIndicator('diligence', '데이터 입력', 10, totalActions, score, hint);
}

// 6. 재고 회전율 (10점)
function scoreInventoryHealth({ prices, inventory, orderHistory, categories, payrollSettings }) {
  if (!prices || prices.length === 0) return makeIndicator('inventory', '재고 회전율', 10, null, 0, '단가표 등록 필요');
  const orderUsage = calcAvgUsage(orderHistory, 3);
  const recipeUsage = calcRecipeBasedUsage(categories, prices, payrollSettings?.expectedMonthlyServings);
  const merged = mergeUsage(orderUsage, recipeUsage);
  const inv = inventory?.items || {};

  let evaluable = 0, needOrder = 0;
  for (const p of prices) {
    const stockEntry = inv[p.name];
    if (!stockEntry || stockEntry.currentStock === undefined) continue;  // 미입력
    const u = merged[p.name] || { avgDaily: 0 };
    const status = getStatus(stockEntry.currentStock ?? 0, stockEntry.safetyStock ?? 0, u.avgDaily);
    evaluable++;
    if (status.icon === '🔴' || status.icon === '🚨' || status.icon === '⚠️') needOrder++;
  }
  if (evaluable === 0) return makeIndicator('inventory', '재고 회전율', 10, null, 0, '재고 입력 필요');
  const dangerRatio = needOrder / evaluable;
  // 위험 0% 만점, 30%↑ 0점
  const score = dangerRatio <= 0 ? 10 : dangerRatio >= 0.3 ? 0 : ((0.3 - dangerRatio) / 0.3) * 10;
  const hint = dangerRatio <= 0.05 ? '✅ 안정적'
    : dangerRatio <= 0.15 ? '⚠️ 일부 발주 필요'
    : '🔴 다수 항목 발주 필요 — 재고관리 탭 확인';
  return makeIndicator('inventory', '재고 회전율', 10, dangerRatio * 100, score, hint);
}

// 7. 발주 안정성 (5점)
function scoreOrderStability({ prices, orderHistory }) {
  if (!prices || prices.length === 0) return makeIndicator('stability', '발주 안정성', 5, null, 0, '단가표 등록 필요');
  const recentOrders = (orderHistory || []).slice(-5);
  if (recentOrders.length === 0) return makeIndicator('stability', '발주 안정성', 5, null, 2.5, '아직 발주 내역 없음');
  // 최근 5건의 발주에서 단가표에 없는 항목 비율 (미매칭)
  const priceNames = new Set(prices.map(p => p.name));
  let total = 0, missing = 0;
  for (const order of recentOrders) {
    for (const item of (order.items || [])) {
      total++;
      if (!priceNames.has(item.name)) missing++;
    }
  }
  if (total === 0) return makeIndicator('stability', '발주 안정성', 5, null, 2.5, '발주 항목 없음');
  const matchRate = 1 - missing / total;
  const score = matchRate * 5;
  const hint = matchRate >= 0.95 ? '✅ 매우 안정적'
    : matchRate >= 0.8 ? '⚠️ 일부 미매칭'
    : '🔴 단가표 등록 누락 다수';
  return makeIndicator('stability', '발주 안정성', 5, matchRate * 100, score, hint);
}

// ── 헬퍼 ──
function sumRevenue(record) {
  const r = record?.revenue;
  if (!r) return 0;
  return (Number(r.card) || 0) + (Number(r.cash) || 0) + (Number(r.delivery) || 0);
}

// 현재 진행 중인 월이면 매출 run-rate 외삽 — 월 단위 비용(인건비 등)과 같은 기간 비교
function projectedRevenue(record, monthKey) {
  const actual = sumRevenue(record);
  if (!monthKey || actual <= 0) return actual;
  const now = new Date();
  const todayMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (monthKey !== todayMonthKey) return actual;
  const todayDay = now.getDate();
  if (todayDay < 7) return actual;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return Math.round(actual * (daysInMonth / todayDay));
}
function sumCost(record) {
  const c = record?.costs;
  if (!c) return 0;
  return (Number(c.ingredients) || 0) + (Number(c.labor) || 0)
    + (Number(c.rent) || 0) + (Number(c.utilities) || 0) + (Number(c.other) || 0);
}

// ── 메인 함수 ──
export function calcStoreHealthScore({
  profitLoss = {},
  monthKey,
  prevMonthKey,
  prices = [],
  inventory = {},
  orderHistory = [],
  categories = [],
  payrollSettings = {},
  transactions = [],
}) {
  const record = profitLoss?.[monthKey] || {};
  const prevRecord = profitLoss?.[prevMonthKey] || {};

  // V170: 투자비 감가상각비는 순이익률에 넣고, 투자 회수·누적 순이익률은 참고로 같이 (점수에는 안 넣음)
  const inv = investmentsOf(profitLoss);
  const dep = depForMonth(inv.items, monthKey);
  let investment = null;
  const start = startMonthOf(inv);
  if (inv.items.length && start && monthKey && monthDiff(start, monthKey) >= 0) {
    const n = Math.min(60, monthDiff(start, monthKey) + 1);
    const rows = [];
    for (let i = n - 1; i >= 0; i--) {
      const m = addMonths(monthKey, -i);
      const rec = profitLoss?.[m] || {};
      const d = depForMonth(inv.items, m);
      rows.push({ month: m, revenue: sumRevenue(rec), costs: sumCost(rec) + d, dep: d });
    }
    investment = paybackSummary(inv.items, rows, monthKey);
  }

  const indicators = [
    scoreNetProfitMargin(record, monthKey, dep),
    scoreFoodCost(record, monthKey),
    scoreLaborCost(record, monthKey),
    scoreRevenueGrowth(record, prevRecord, { transactions, monthKey, prevMonthKey }),
    scoreDataDiligence({ orderHistory, monthKey, inventory, prices }),
    scoreInventoryHealth({ prices, inventory, orderHistory, categories, payrollSettings }),
    scoreOrderStability({ prices, orderHistory }),
  ];

  const total = indicators.reduce((s, i) => s + i.score, 0);
  const totalRounded = Math.round(total);

  let status, color, label, message;
  if (totalRounded >= 80) {
    status = 'good'; color = '#FF6F0F'; label = '🟢 우수';
    message = '이번 달 매장 건강 양호합니다';
  } else if (totalRounded >= 60) {
    status = 'normal'; color = '#EA580C'; label = '🟡 보통';
    message = '개선 영역을 점검해보세요';
  } else {
    status = 'warn'; color = '#52525B'; label = '🔴 주의';
    message = '즉시 점검이 필요한 항목이 있어요';
  }

  return {
    total: totalRounded,
    indicators,
    investment,
    status,
    color,
    label,
    message,
  };
}
